class NihooyaSlideshow extends HTMLElement {
  constructor() {
    super();
    this.current = 0;
    this.timer = null;
    this.progressRaf = null;
    this.progressStart = 0;
    this.isPlaying = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slideshow-slide]'));
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.speed, 10) || 5) * 1000;

    if (this.slides.length === 0) return;

    if (this.slides.length === 1) {
      this.slides[0].classList.add('is-active');
      this.slides[0].setAttribute('aria-hidden', 'false');
      this.animateSlideContent(this.slides[0]);
      this.querySelector('.nihooya-slideshow__chrome')?.setAttribute('hidden', '');
      return;
    }

    this.goTo(0, false);

    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    if (this.autoplayEnabled) this.play();
  }

  disconnectedCallback() {
    this.pause();
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.current - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.current + 1);
    }
  }

  onTouchStart(event) {
    const touch = event.changedTouches[0];
    this.touchStartX = touch.screenX;
    this.touchStartY = touch.screenY;
  }

  onTouchEnd(event) {
    const touch = event.changedTouches[0];
    const deltaX = touch.screenX - this.touchStartX;
    const deltaY = touch.screenY - this.touchStartY;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) {
      this.goTo(this.current + 1);
    } else {
      this.goTo(this.current - 1);
    }
  }

  animateSlideContent(slide) {
    const inner = slide.querySelector('.nihooya-slideshow__content-inner');
    if (!inner || this.reducedMotion) return;

    inner.classList.remove('is-hero-animating');
    void inner.offsetWidth;
    inner.classList.add('is-hero-animating');
  }

  goTo(index, animate = true) {
    const total = this.slides.length;
    const nextIndex = ((index % total) + total) % total;

    this.slides.forEach((slide, i) => {
      const isActive = i === nextIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (isActive) {
        this.animateSlideContent(slide);
      } else {
        slide.querySelector('.nihooya-slideshow__content-inner')?.classList.remove('is-hero-animating');
      }
    });

    this.current = nextIndex;

    if (animate && this.autoplayEnabled) {
      this.pause();
      this.play();
    } else if (this.isPlaying) {
      this.startProgress();
    } else if (this.progressFill) {
      this.progressFill.style.width = '0%';
    }
  }

  play() {
    if (!this.autoplayEnabled || this.reducedMotion || this.slides.length < 2) return;
    this.pause();
    this.isPlaying = true;
    this.startProgress();
    this.timer = window.setTimeout(() => {
      this.goTo(this.current + 1, false);
      this.play();
    }, this.autoplaySpeed);
  }

  pause() {
    this.isPlaying = false;
    window.clearTimeout(this.timer);
    this.timer = null;
    this.stopProgress();
  }

  resume() {
    if (this.autoplayEnabled && !this.reducedMotion) this.play();
  }

  startProgress() {
    this.stopProgress();
    if (!this.progressFill) return;

    this.progressFill.style.transition = 'none';
    this.progressFill.style.width = '0%';
    this.progressStart = performance.now();

    const tick = (now) => {
      const elapsed = now - this.progressStart;
      const pct = Math.min((elapsed / this.autoplaySpeed) * 100, 100);
      this.progressFill.style.width = `${pct}%`;

      if (pct < 100 && this.isPlaying) {
        this.progressRaf = requestAnimationFrame(tick);
      }
    };

    this.progressRaf = requestAnimationFrame(tick);
  }

  stopProgress() {
    if (this.progressRaf) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
  }
}

customElements.define('nihooya-slideshow', NihooyaSlideshow);